"use client";

import { c as _c } from "react/compiler-runtime";
import { assertUnreachable } from "@/helpers/AllowlistToolHelpers";
import InteractiveMediaLoadGate from "@/components/drops/media/InteractiveMediaLoadGate";
import dynamic from "next/dynamic";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SandboxedExternalIframe from "@/components/common/SandboxedExternalIframe";
import { getMediaGatewayFallbackUrls, shouldUseIframeFallbackTimeout } from "@/components/nft-image/utils/gateway-fallback";
import { ImageScale } from "@/helpers/image.helpers";
import useCapacitor from "@/hooks/useCapacitor";
import MediaDisplayAudio from "./MediaDisplayAudio";
import MediaDisplayImage from "./MediaDisplayImage";
import MediaDisplayVideo from "./MediaDisplayVideo";
import { InlineMediaActions } from "./MediaActionToolbar";
import type { MediaLoadStrategy } from "./mediaLoadStrategy";
import UnsupportedMediaLink from "./UnsupportedMediaLink";
enum MediaType {
  IMAGE = "IMAGE",
  VIDEO = "VIDEO",
  AUDIO = "AUDIO",
  GLB = "GLB",
  HTML = "HTML",
  UNKNOWN = "UNKNOWN",
}
const MediaDisplayGLB = dynamic(() => import("./MediaDisplayGLB"), {
  ssr: false
});
const DEFAULT_HTML_MEDIA_TITLE = "Interactive HTML media";
const IFRAME_FALLBACK_TIMEOUT_MS = 8000;
function InteractiveHtmlMediaDisplay(t0) {
  const $ = _c(40);
  const {
    media_url,
    previewImageUrl,
    imageScale: t1,
    requireInteractionToLoad: t2,
    iframeContainerClassName
  } = t0;
  const imageScale = t1 === undefined ? ImageScale.AUTOx1080 : t1;
  const requireInteractionToLoad = t2 === undefined ? false : t2;
  const {
    isCapacitor
  } = useCapacitor();
  const iframeRef = useRef(null);
  const [isActivated, setIsActivated] = useState(!requireInteractionToLoad);
  let t3;
  if ($[0] !== media_url) {
    t3 = getMediaGatewayFallbackUrls(media_url);
    $[0] = media_url;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const urls = t3;
  const [activeIndex, setActiveIndex] = useState(0);
  const [didLoadCurrentUrl, setDidLoadCurrentUrl] = useState(false);
  const [isIframeVisible, setIsIframeVisible] = useState(false);
  const activeUrl = urls[activeIndex];
  let t4;
  if ($[2] !== requireInteractionToLoad) {
    t4 = () => {
      setIsActivated(!requireInteractionToLoad);
    };
    $[2] = requireInteractionToLoad;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== media_url || $[5] !== requireInteractionToLoad) {
    t5 = [media_url, requireInteractionToLoad];
    $[4] = media_url;
    $[5] = requireInteractionToLoad;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setActiveIndex(0);
      setDidLoadCurrentUrl(false);
      setIsIframeVisible(false);
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== urls) {
    t7 = [urls];
    $[8] = urls;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  useEffect(t6, t7);
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setDidLoadCurrentUrl(false);
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== activeUrl) {
    t9 = [activeUrl];
    $[11] = activeUrl;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[13] !== activeIndex || $[14] !== activeUrl || $[15] !== didLoadCurrentUrl || $[16] !== isIframeVisible || $[17] !== urls.length) {
    t10 = () => {
      if (!activeUrl || !isIframeVisible || didLoadCurrentUrl || activeIndex + 1 >= urls.length || !shouldUseIframeFallbackTimeout(activeUrl)) {
        return;
      }
      const timeoutId = globalThis.setTimeout(() => {
        setActiveIndex(current => current === activeIndex && current + 1 < urls.length ? current + 1 : current);
      }, IFRAME_FALLBACK_TIMEOUT_MS);
      return () => {
        globalThis.clearTimeout(timeoutId);
      };
    };
    t11 = [activeIndex, activeUrl, didLoadCurrentUrl, isIframeVisible, urls.length];
    $[13] = activeIndex;
    $[14] = activeUrl;
    $[15] = didLoadCurrentUrl;
    $[16] = isIframeVisible;
    $[17] = urls.length;
    $[18] = t10;
    $[19] = t11;
  } else {
    t10 = $[18];
    t11 = $[19];
  }
  useEffect(t10, t11);
  let t12;
  if ($[20] !== urls.length) {
    t12 = () => {
      setActiveIndex(current_0 => current_0 + 1 < urls.length ? current_0 + 1 : current_0);
    };
    $[20] = urls.length;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const advanceToNextUrl = t12;
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      iframeRef.current?.requestFullscreen().catch(_temp);
    };
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const handleFullScreen = t13;
  if (requireInteractionToLoad && !isActivated) {
    let t14;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = () => setIsActivated(true);
      $[23] = t14;
    } else {
      t14 = $[23];
    }
    let t15;
    if ($[24] !== imageScale || $[25] !== previewImageUrl) {
      t15 = previewImageUrl ? <MediaDisplayImage src={previewImageUrl} imageScale={imageScale} /> : null;
      $[24] = imageScale;
      $[25] = previewImageUrl;
      $[26] = t15;
    } else {
      t15 = $[26];
    }
    let t16;
    if ($[27] !== t15) {
      t16 = <InteractiveMediaLoadGate onLoad={t14}>{t15}</InteractiveMediaLoadGate>;
      $[27] = t15;
      $[28] = t16;
    } else {
      t16 = $[28];
    }
    return t16;
  }
  if (!activeUrl) {
    return null;
  }
  const t14 = !isCapacitor;
  let t15;
  if ($[29] !== t14) {
    t15 = <InlineMediaActions variant="html" isDownloading={false} onFullscreen={handleFullScreen} fullscreenTargetAvailable={t14} position="bottom-right" />;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => {
      setDidLoadCurrentUrl(true);
    };
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => {
      setIsIframeVisible(true);
    };
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== activeUrl || $[34] !== advanceToNextUrl || $[35] !== iframeContainerClassName) {
    t18 = <SandboxedExternalIframe key={activeUrl} title={DEFAULT_HTML_MEDIA_TITLE} src={activeUrl} className="tw-h-full tw-w-full" containerClassName={iframeContainerClassName} iframeRef={iframeRef} onLoad={t16} onError={advanceToNextUrl} onVisible={t17} />;
    $[33] = activeUrl;
    $[34] = advanceToNextUrl;
    $[35] = iframeContainerClassName;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t15 || $[38] !== t18) {
    t19 = <div className="tw-relative tw-h-full tw-w-full">{t15}{t18}</div>;
    $[37] = t15;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  return t19;
}

/**
 * A component to display media content without interactive modal functionality.
 * Identical to DropListItemContentMedia but without art viewing modal functionality.
 * Use this when you want to simply display media in a container that has its own click behavior.
 *
 * When previewImageUrl is provided, it will be shown instead of the original media
 * for non-image media types (video, audio, HTML, GLB). This is useful for showing
 * static preview images in gallery/thumbnail contexts.
 *
 * When requireInteractionToLoad is true for HTML media, previewImageUrl becomes
 * the poster shown behind the load gate instead of permanently replacing the HTML.
 */
function _temp() {}
export default function MediaDisplay({
  media_mime_type,
  media_url,
  disableMediaInteraction = false,
  imageScale = ImageScale.AUTOx1080,
  previewImageUrl,
  requireInteractionToLoad = false,
  iframeContainerClassName,
  loadStrategy = "in-view"
}: {
  readonly media_mime_type: string;
  readonly media_url: string;
  readonly disableMediaInteraction?: boolean | undefined;
  readonly imageScale?: ImageScale | undefined;
  readonly previewImageUrl?: string | null | undefined;
  readonly requireInteractionToLoad?: boolean | undefined;
  readonly iframeContainerClassName?: string | undefined;
  readonly loadStrategy?: MediaLoadStrategy | undefined;
}) {
  const getMediaType = (): MediaType => {
    if (media_mime_type.includes("image")) {
      return MediaType.IMAGE;
    }
    if (media_mime_type.includes("video")) {
      return MediaType.VIDEO;
    }
    if (media_mime_type.includes("audio")) {
      return MediaType.AUDIO;
    }
    if (media_mime_type === "model/gltf-binary" || media_mime_type === "model/gltf+json" || media_url.endsWith(".glb") || media_url.endsWith(".gltf")) {
      return MediaType.GLB;
    }
    if (media_mime_type === "text/html") {
      return MediaType.HTML;
    }
    return MediaType.UNKNOWN;
  };
  const mediaType = getMediaType();
  if (mediaType === MediaType.HTML) {
    if (loadStrategy === "eager") {
      return previewImageUrl ? <MediaDisplayImage src={previewImageUrl} imageScale={imageScale} loadStrategy={loadStrategy} /> : null;
    }
    if (previewImageUrl && !requireInteractionToLoad) {
      return <MediaDisplayImage src={previewImageUrl} imageScale={imageScale} loadStrategy={loadStrategy} />;
    }
    return <InteractiveHtmlMediaDisplay key={`${media_mime_type}:${media_url}`} media_url={media_url} previewImageUrl={previewImageUrl} imageScale={imageScale} requireInteractionToLoad={requireInteractionToLoad} iframeContainerClassName={iframeContainerClassName} />;
  }
  if (previewImageUrl && mediaType !== MediaType.IMAGE) {
    return <MediaDisplayImage src={previewImageUrl} imageScale={imageScale} loadStrategy={loadStrategy} />;
  }
  switch (mediaType) {
    case MediaType.IMAGE:
      return <MediaDisplayImage src={media_url} imageScale={imageScale} loadStrategy={loadStrategy} />;
    case MediaType.VIDEO:
      return <MediaDisplayVideo src={media_url} mimeType={media_mime_type} showControls={!disableMediaInteraction} />;
    case MediaType.AUDIO:
      return <MediaDisplayAudio src={media_url} showControls={!disableMediaInteraction} />;
    case MediaType.GLB:
      return <MediaDisplayGLB src={media_url} disableMediaInteractions={disableMediaInteraction} />;
    case MediaType.UNKNOWN:
      return <UnsupportedMediaLink media_url={media_url} disableMediaInteraction={disableMediaInteraction} />;
    default:
      assertUnreachable(mediaType);
      return;
  }
}